/**
 * Dashboard feature configuration.
 * - FEATURES: env-based flags (VITE_FEATURE_ENABLE_*) for deploy-time toggles
 * - FEATURE_FLAGS: Unleash keys for runtime toggles via useFlag()
 */

/**
 * Checks whether an env-based feature flag is enabled.
 * Flags must be set to the string 'true' in .env (e.g. VITE_FEATURE_ENABLE_X=true).
 * Add new flags to ImportMetaEnv in vite-env.d.ts for type safety.
 */
export function isFeatureEnabled<K extends keyof ImportMetaEnv>(flag: K): boolean {
  return import.meta.env[flag] === 'true';
}

/**
 * Env-based feature flags. Use for build-time or deploy-time toggles controlled
 * via environment variables. Add entries as needed:
 *
 * 1. Add the env var to ImportMetaEnv in vite-env.d.ts
 * 2. Add the flag here: FEATURE_NAME: isFeatureEnabled('VITE_FEATURE_ENABLE_FEATURE_NAME'),
 * 3. Add to .env.example: VITE_FEATURE_ENABLE_FEATURE_NAME=true
 */
export const FEATURES = {
  // Example:
  // USER_REGION: isFeatureEnabled('VITE_FEATURE_ENABLE_USER_REGION'),
};

/**
 * Unleash feature flag keys. Use with useFlag() for runtime toggles from Unleash.
 * Distinct from FEATURES, which are env-based and evaluated at build time.
 */
export const FEATURE_FLAGS = {
  MULTI_SOURCE: 'dashboard-feature-multi-source',
  DOCUMENT_TYPE: 'dashboard-feature-document-type',
  DATASETS_2_0: 'dashboard-datasets-2-0',
  FORESTER_DEMO: 'forester-demo',
  UNIFIED_INTEGRATION: 'dashboard-feature-unified-integration',
  DIGITAL_MODEL_DELETE: 'dashboard-digital-model-delete',
  DIGITAL_MODEL_REFRESH: 'dashboard-digital-model-refresh',
  CUSTOM_SHARE: 'dashboard-custom-share',
  SCHEDULED_MAINTENANCE_BANNER: 'dashboard-scheduled-maintenance-banner',
  SOURCE_DIGITAL_DEEPLINK_BLOCKED: 'dashboard-source-digital-deeplink',
  LABEL_MANAGEMENT: 'dashboard-label-management',
  SPARK: 'dashboard-navigation-spark',
} as const;
